import { Autocomplete, Grid, Slider, TextField, Tooltip } from '@mui/material';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { numberToCurrencyString } from 'further-ui/utils';
import { InvestmentPaymentStatus, InvestorTag } from 'further-types/investment';
import { InvestmentPaymentStatusLabels } from 'further-ui/labels';
import Spacing from 'components/Spacing';
import { FieldRow } from 'components/FormElements';
import GridContainer from 'components/GridContainer';
import MultiCustomTagSelector from 'components/MultiCustomTagSelector';

const ValueLabelComponent = ({ children, open, value }) => (
  <Tooltip
    open={open}
    enterTouchDelay={0}
    placement="bottom"
    title={`${value}`}
  >
    {children}
  </Tooltip>
);
const paymentMethod = [
  InvestmentPaymentStatus.FullyFunded,
  InvestmentPaymentStatus.PartiallyFunded,
  InvestmentPaymentStatus.NoPayment,
].map((status) => ({
  paymentStatus: status,
  text: InvestmentPaymentStatusLabels[status],
}));

type Props = any;

const FilterPanel: React.FC<Props> = ({
  filters,
  handleChangeFilters,
  errorText,
}) => {
  const informationText = `Range: ${numberToCurrencyString(
    filters.amount?.[0],
    {
      hidePennies: true,
    },
  )} - ${numberToCurrencyString(filters.amount?.[1], {
    hidePennies: true,
  })}`;

  const investorTagOptions = [
    {
      label: 'Hard copy',
      value: InvestorTag.RequiresHardCopyReports,
    },
    {
      label: 'Vulnerable client',
      value: InvestorTag.VulnerableClient,
    },
    {
      label: 'Deceased',
      value: InvestorTag.Deceased,
    },
  ];

  return (
    <Spacing>
      <FieldRow
        title="Subscription amount"
        informationText={informationText}
        fullWidth
      >
        <Slider
          slots={{
            valueLabel: ValueLabelComponent,
          }}
          value={[
            Number(filters.amount?.[0] ?? 0),
            Number(filters.amount?.[1] ?? 2000000),
          ]}
          onChange={(_, newValue) => {
            handleChangeFilters({ amount: newValue });
          }}
          valueLabelDisplay="auto"
          min={1}
          step={1}
          max={2000000}
          track={false}
        />
      </FieldRow>
      <FieldRow title="Payment status" fullWidth>
        <AppSelectBox
          fullWidth
          data={paymentMethod}
          placeholder="Search by payment status"
          valueKey="paymentStatus"
          variant="outlined"
          labelKey="text"
          name="paymentStatus"
          value={filters.paymentStatus}
          onChange={(e) => {
            handleChangeFilters({
              paymentStatus: e.target.value,
            });
          }}
        />
      </FieldRow>
      <GridContainer>
        <Grid size={{ md: 6, xs: 12 }}>
          <FieldRow title="Start date" fullWidth>
            <AppDatePicker
              required
              name="startDate"
              label=""
              value={filters.startDate}
              clearable={true}
              maxDate={filters.endDate ? filters.endDate : undefined}
              onChange={(date) => {
                handleChangeFilters({ startDate: date });
              }}
              error={!!errorText.startDate}
              helperText={errorText.startDate}
            />
          </FieldRow>
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <FieldRow title="End date" fullWidth>
            <AppDatePicker
              required
              name="endDate"
              label=""
              value={filters.endDate}
              clearable={true}
              onChange={(date) => {
                handleChangeFilters({ endDate: date });
              }}
              minDate={filters.startDate ? filters.startDate : undefined}
              error={!!errorText.endDate}
              helperText={errorText.endDate}
            />
          </FieldRow>
        </Grid>
      </GridContainer>
      <FieldRow title="Custom tags" fullWidth>
        <MultiCustomTagSelector
          mode="subscription"
          firmId={filters.firmId}
          onChange={(value) => {
            handleChangeFilters({ customTags: value });
          }}
          value={filters.customTags ?? []}
        />
      </FieldRow>
      <FieldRow title="Investor tags" fullWidth>
        <Autocomplete
          multiple
          size="small"
          options={investorTagOptions}
          value={filters.investorTags?.map((tag: InvestorTag) =>
            investorTagOptions.find((option) => option.value === tag),
          )}
          onChange={(_, newValue) => {
            handleChangeFilters({
              investorTags: newValue.map((o) => o.value),
            });
          }}
          getOptionLabel={(option) => option.label}
          getOptionKey={(option) => option.value}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select investor tags"
              variant="outlined"
            />
          )}
        />
      </FieldRow>
    </Spacing>
  );
};
export default FilterPanel;
